import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Calculator, Loader2, Sparkles, Download, ShieldCheck, AlertTriangle, CheckCircle2,
} from "lucide-react";

const fmt = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

export const SalaryTool = () => {
  const [inputs, setInputs] = useState({ business_description: "", hours_per_week: "", years_experience: "", duties: "" });
  const [rec, setRec] = useState(null);
  const [memo, setMemo] = useState(null);
  const [chosen, setChosen] = useState("");
  const [source, setSource] = useState("recommended");
  const [cpaNote, setCpaNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/intake/salary").then(({ data }) => {
      if (data.recommendation) setRec(data.recommendation);
      if (data.inputs) setInputs((p) => ({ ...p, ...data.inputs }));
      if (data.memo) setMemo(data.memo);
      if (data.chosen_salary) setChosen(String(data.chosen_salary));
    }).catch(() => {});
  }, []);

  const getRecommendation = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/intake/salary/recommend", inputs);
      setRec(data);
      setSource("recommended");
      setChosen(String(data.recommended_mid || ""));
      toast.success("AI recommendation ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate a recommendation");
    } finally { setLoading(false); }
  };

  const generateMemo = async () => {
    const amt = Number(chosen);
    if (!amt || amt <= 0) { toast.error("Choose or enter a salary amount first."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/intake/salary/memo", { chosen_salary: amt, source, cpa_note: cpaNote });
      setMemo(data);
      toast.success("Reasonable Compensation Memo saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the memo");
    } finally { setSaving(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/files/${memo.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = memo.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download memo"); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="salary-tool">
      <div className="flex items-center gap-2">
        <Calculator className="h-5 w-5 text-[#114A31]" />
        <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">Reasonable Salary Analyzer</h2>
      </div>
      <p className="text-sm text-[#595D5A] mt-1">
        The #1 S-Corp audit trigger. Get an AI-backed reasonable salary and a dated Compensation Memo for your records.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input value={inputs.business_description} onChange={(e) => setInputs({ ...inputs, business_description: e.target.value })}
          placeholder="What does your business do?" data-testid="salary-biz" className="rounded-xl border-[#E5E5E5]" />
        <Input value={inputs.duties} onChange={(e) => setInputs({ ...inputs, duties: e.target.value })}
          placeholder="Your main duties (e.g., sales, ops)" data-testid="salary-duties" className="rounded-xl border-[#E5E5E5]" />
        <Input value={inputs.hours_per_week} onChange={(e) => setInputs({ ...inputs, hours_per_week: e.target.value })}
          placeholder="Hours per week (e.g., 40)" data-testid="salary-hours" className="rounded-xl border-[#E5E5E5]" />
        <Input value={inputs.years_experience} onChange={(e) => setInputs({ ...inputs, years_experience: e.target.value })}
          placeholder="Years of experience" data-testid="salary-experience" className="rounded-xl border-[#E5E5E5]" />
      </div>
      <Button onClick={getRecommendation} disabled={loading} data-testid="salary-recommend-btn"
        className="mt-4 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analyzing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Get AI recommendation</>}
      </Button>

      {rec && (
        <div className="mt-5 rounded-2xl border border-[#E6F0EB] bg-[#F9F9F8] p-5" data-testid="salary-result">
          <p className="text-xs uppercase tracking-wide text-[#595D5A]">Recommended reasonable salary</p>
          <div className="mt-1 flex items-end gap-3 flex-wrap">
            <span className="font-heading text-3xl font-bold text-[#114A31]">{fmt(rec.recommended_mid)}</span>
            <span className="text-sm text-[#595D5A]">range {fmt(rec.recommended_low)} – {fmt(rec.recommended_high)} · net profit {fmt(rec.net_profit)}</span>
          </div>
          {rec.summary && <p className="mt-3 text-sm text-[#1C1C1C]">{rec.summary}</p>}

          {rec.factors?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-[#114A31] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> IRS factor analysis</p>
              <ul className="mt-2 space-y-1.5">
                {rec.factors.map((f, i) => (
                  <li key={i} className="text-sm text-[#595D5A]"><span className="text-[#1C1C1C] font-medium">{f.factor}:</span> {f.analysis}</li>
                ))}
              </ul>
            </div>
          )}
          {rec.red_flags?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-[#92600A] flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Audit red flags</p>
              <ul className="mt-2 space-y-1">
                {rec.red_flags.map((r, i) => <li key={i} className="text-sm text-[#595D5A]">• {r}</li>)}
              </ul>
            </div>
          )}

          {/* Choose salary */}
          <div className="mt-5 pt-4 border-t border-[#E6F0EB]">
            <p className="text-sm font-medium text-[#1C1C1C]">Set your salary</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["recommended_low", "Low"], ["recommended_mid", "Recommended"], ["recommended_high", "High"]].map(([k, lbl]) => (
                <button key={k} onClick={() => { setChosen(String(rec[k])); setSource("recommended"); }}
                  data-testid={`salary-pick-${k}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${String(rec[k]) === chosen && source === "recommended" ? "bg-[#114A31] text-white border-[#114A31]" : "border-[#E5E5E5] text-[#1C1C1C] hover:border-[#114A31]"}`}>
                  {lbl} · {fmt(rec[k])}
                </button>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={chosen} onChange={(e) => { setChosen(e.target.value.replace(/[^0-9]/g, "")); setSource("cpa"); }}
                placeholder="Or enter your CPA's number" data-testid="salary-custom" className="rounded-xl border-[#E5E5E5]" />
              {source === "cpa" && (
                <Input value={cpaNote} onChange={(e) => setCpaNote(e.target.value)}
                  placeholder="CPA note / source (optional)" data-testid="salary-cpa-note" className="rounded-xl border-[#E5E5E5]" />
              )}
            </div>
            <Button onClick={generateMemo} disabled={saving} data-testid="salary-memo-btn"
              className="mt-4 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : <>Generate Compensation Memo</>}
            </Button>
          </div>
        </div>
      )}

      {memo && (
        <div className="mt-4 flex items-center gap-3 text-sm bg-[#E6F0EB] rounded-xl px-4 py-3" data-testid="salary-memo-file">
          <CheckCircle2 className="h-4 w-4 text-[#166534]" />
          <span className="flex-1 text-[#114A31]">Reasonable Compensation Memo saved{memo.chosen_salary ? ` · ${fmt(memo.chosen_salary)}` : ""}</span>
          <button onClick={download} data-testid="salary-memo-download" className="text-[#114A31] hover:text-[#1A6B46]"><Download className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
};
